import {MuWidget} from "mu-widget/lib/MuWidget";
import {dataStore, DataStore} from "../../DataStore";
import {DateTime} from "mu-js-utils/lib/DateTime";
import {MuBinder} from "mu-widget/lib/MuBinder";
import {JournalActivity, PlanItem, WatchedActivity} from "../../types";
import {Triggers} from "mu-js-utils/lib/Triggers";
import {TNames} from "../../TNames";
import {UiApp} from "./UiApp";
import {UiFlashContainer} from "mu-widget/lib/components/UiFlash";
import {Arrays} from "mu-widget/lib/utils/Arrays";
import {JournalActivityUtils} from "../../JournalActivityUtils";
import {addFlash, formatEnergy, isToday} from "../../utils";
import {frontRouter} from "../../FrontRouter";
import {sleepPromise} from "mu-js-utils/lib/utils";

export class UiJournalActivityList extends MuWidget
{
    // @ts-ignore
    muParent: UiApp;

    beforeIndex() {
        this.muAppendContent(`
            <div class="d-flex-row">
                <span mu="bPrevDay" class="icon--prev icon-size--2rem a"></span>
                <input type="date" mu="date" />
                <span class="icon--today icon-size--2rem" mu="bToday"></span>
                <span mu="bNextDay" class="icon--next icon-size--2rem a"></span>
            </div>
            <div mu="list" class="journal-activity__list">
                <div class="journal-activity__hour" mu=":.@hour"><span mu="#hour"></span></div>
                <div class="journal-activity__items" mu=":.@items"></div>
                <div class="journal-activity__plan" mu=":.@plan"></div>
                <div class="journal-activity__item" mu=":UiJournalActivityItem@item"></div>
            </div>
            <span class="l-app-bar__stop-watch cursor-pointer" mu="bStopWatch">
                <span class="icon-size--2rem icon--stop"></span>
                <span mu="watchActivityName"></span> &nbsp;
                <em mu="watchActivityDuration"></em>
            </span>
        `)
    }

    public watchedActivity: WatchedActivity|null = null;

    async afterIndex() {
        this.ui.date.value = DateTime.dateToInput(new Date(), "day");
        this.muVisible(false, 'bStopWatch');
        this.muExposeElement('bStopWatch', this.muParent.ui.pBarMiddle);

        await this.loadJournalActivityList();
        Triggers.addHandler([
            TNames.JournalActivityChanged,
            TNames.clientDataChanged,
        ], () => this.loadJournalActivityList());
        Triggers.addHandler(
            TNames.Minute,
            () => {
                this.muGetChildWidgets<UiJournalActivityItem>('list')
                    .forEach(w => w.doRefresh && w.doRefresh())
                this.updateWatchedActivityDuration();
            }
        );
    }

    bToday_click() {
        const d = new Date();
        this.ui.date.value = DateTime.dateToInput(d, "day");
        this.loadJournalActivityList();
    }

    private updateWatchedActivityDuration() {
        if (this.watchedActivity) {
            this.ui.watchActivityDuration.textContent = JournalActivityUtils.formatCurrentDuration(this.watchedActivity);
        }
        // notifier.updateWatch(this.watchedActivity);
    }

    async loadJournalActivityList() {
        const data = await dataStore.getMyJournal(this.ui.date.value);
        const day = new Date(this.ui.date.value + ' 00:00');
        const newDay = DateTime.format(day, 'Y-m-d');
        this.muVisible(!isToday(newDay), 'bToday');
        if (frontRouter.lastParameters.day !== newDay) {
            frontRouter.pushUpdate('day', { day: newDay });
        }
        const dayUt = day.getTime() / 1000;
        const planItems = await DataStore.get().getMyPlansItemsForDay(this.ui.date.value);
        Triggers.dispatchAsync(TNames.dayLoaded, { ...data, plan: planItems, day: this.ui.date.value });
        const planItemsByHour = Arrays.groupBy(
            planItems,
            pi => Math.floor(pi.repeating.secondsAM / 3600)
        );
        const itemsByHour= Arrays.groupBy(
            data.activities,
            item => Math.floor((new Date(item.dtStart).getTime() / 1000 - dayUt) / 3600)
        );
        this.watchedActivity = data.watchedActivity;
        this.ui.list.innerHTML = '';
        const hours = new Set(
            [ ...Object.keys(planItemsByHour), ...Object.keys(itemsByHour) ]
                .map(n => parseInt(n))
                .sort((a, b) => a - b)
        );
        for (const hour of hours) {
            const strHour = (hour < 0
                ? '<< ' + (hour - (Math.floor(hour / 24) * 24)).toString()
                : hour.toString()) + ':00';
            const hourContainer = this.muWidgetFromTemplate('hour', 'list');
            hourContainer.muBindData({ hour: strHour });
            const itemsContainer = this.muWidgetFromTemplate('items', 'list').container;
            const planContainer = this.muWidgetFromTemplate('plan', 'list').container;

            for (const item of itemsByHour[hour] ?? []) {
                this.muWidgetFromTemplate('item', itemsContainer)
                    .muBindData(item);
            }

            for (const item of planItemsByHour[hour] ?? []) {
                this.muWidgetFromTemplate('item', planContainer,{ type: 'plan', day: day })
                    .muBindData(item);
            }
        }
        this.muVisible(!!data.watchedActivity, 'bStopWatch');
        if (data.watchedActivity) {
            this.ui.watchActivityName.textContent = data.watchedActivity.name;

        }
        this.updateWatchedActivityDuration();
    }

    date_change() {
        this.loadJournalActivityList();
    }

    bPrevDay_click() {
        this.changeDay(-1);
    }

    bNextDay_click() {
        this.changeDay(1);
    }

    async bStopWatch_click() {
        if (confirm('Zastavit stopování času?')) {
            await DataStore.get().stopWatching(this.watchedActivity!.journalActivityId);
            UiFlashContainer.add('Stopování času bylo zastaveno', 'success');
            this.loadJournalActivityList();
        }
    }

    private changeDay(diff: number) {
        let d = new Date(this.ui.date.value);
        d.setDate(d.getDate() + diff);
        this.ui.date.value = DateTime.dateToInput(d, "day");
        this.loadJournalActivityList();
    }
}

export class UiJournalActivityItem extends MuWidget
{
    public refresh: boolean = false;

    public canAddNotify: boolean = false;
    // @ts-ignore
    private item: JournalActivity;
    // @ts-ignore
    muParent: UiJournalActivityList;

    public type: 'journal'|'plan' = 'journal';

    public day: Date;

    beforeIndex() {
        this.muAppendContent(`
            <span class="d-flex-column">
                <span mu="#dtStart|formatDate(time)" class="journal-activity__item__dt-start"></span>
                <span mu="dtTo#dtTo|formatDate(time)" class="journal-activity__item__dt-start">00:00</span>
            </span>
            <span mu="#icon|prepend('journal-activity__item__icon icon--'):className"></span>
            <span class="journal-activity__item__dt-to">
                <span mu="value#value"></span><br />
                <span mu="#energy;energy:@visible;energyIcon|prepend('icon-before--'):className"></span>                    
            </span>
                       
            <span class="journal-activity__item__activity">
                <span mu="activityName#activityName"></span>
                <span mu="activityVariantName#activityVariantName|prepend(' / ',true)" class="journal-activity__item__activity-variant"></span>
            </span>
            <span mu="values#values:@foreach" class="journal-activity__item__values">
                <span mu=":.@valueItem">
                    <strong mu="#activityVariantName"></strong>
                    <span>
                    
                    </span>
                    <span mu="#value"></span>
                </span>
            </span>
            <div class="journal-activity__item__note">
                <div mu="#note"></div>
                <div mu="#trainerNotes:@foreach">
                    <div mu=":.@trainerNote" class="journal-activity__item__trainer-note">
                        <span mu="#icon:@visible;icon|prepend('icon-size--2rem journal-activity__item__trainer-note-icon icon--'):className"></span>
                        <div mu="#note;note:@visible" class="journal-activity__item__trainer-note-content"></div>
                    </div>
                </div>
            </div>
            ` + (this.type === 'journal'
                ? `<span class="icon-toolbox icon-toolbox--visible-if-parent __visible-on-hover" mu="toolbox">
                    <span mu="bEdit" class="icon--edit"></span>
                    <span mu="bDelete" class="icon--close"></span>
                    <span mu="bContinue" class="icon--start"></span>
                    <span mu="bNote" class="icon--note"></span>
                </span>`
                : ''
            )
        )
    }

    protected toolsVisible = false;
    container_mouseenter() {
        /* this.toolsVisible = true;
        await sleepPromise(1);
        this.container.classList.add('icon-toolbox--parent-visible'); */
        this.setToolsVisible(true);
    }

    container_mouseleave() {
        /* this.container.classList.remove('icon-toolbox--parent-visible'); */
        this.setToolsVisible(false);
    }

    async container_click() {
        if (!this.toolsVisible) {
            await this.setToolsVisible(true);
            await Triggers.waitFor(TNames.documentClick);
            await this.setToolsVisible(false);
        }
    }

    private async setToolsVisible(visible: boolean) {
        this.toolsVisible = visible;
        await sleepPromise(1);
        this.container.classList[visible ? 'add' : 'remove']('icon-toolbox--parent-visible');
    }

    muBindData(srcData: any) {
        super.muBindData({
            ...srcData,
            value: this.formatValue(srcData),
            dtTo: this.dtTo(srcData),
            dtFrom: this.dtFrom(srcData),
            activityVariantName: srcData.activityVariantName ?? srcData.customValues?.name ?? null,
            energy: srcData.energyValues?.energy ? formatEnergy(srcData.energyValues?.energy) : null,
            energyIcon: {activity: 'fire', food: 'apple'}[srcData.activityCode] ?? '',
        });
        this.item = srcData;
        if (this.item.value === null && this.item.useTimeWatcher) {
            this.ui.activityName.classList.add('icon-before--start');
        }
        this.muVisible(!srcData.values, 'value', 'hidden');
        this.muVisible(!srcData.values, [
            'activityVariantName',
            '!values',
        ]);
        if (this.ui.bContinue) this.muVisible(srcData.activityOptions.useTimeWatcher, 'bContinue');
        if (this.ui.bNote)
            this.muVisible(this.canAddNotify, 'bNote');
    }

    public doRefresh(): void {
        if (this.item.value === null && this.item.useTimeWatcher) {
            super.muBindData({ value: this.formatValue(this.item) });
        }
    }

    private formatValue(srcData: JournalActivity) {
        return srcData.useTimeWatcher
            ? (srcData.value === null
                ? MuBinder.filters.duration((new Date().getTime() - srcData.dtStart.getTime()) / 1000)
                : MuBinder.filters.duration(srcData.value)
            )
            : (srcData.value === null
                ? ''
                : srcData.value + (srcData.activityOptions.unit ?? '')
            )
    }

    async bEdit_click() {
        this.muParent.muParent.editActivity(null, await DataStore.get().getJournalActivity(this.item.id));
    }

    async bDelete_click() {
        if (!confirm('Opravdu smazat tento záznam?')) return;
        await DataStore.get().deleteJournalActivity(this.item.id);
        addFlash('Záznam byl smazán');
        Triggers.dispatch(TNames.JournalActivityChanged);
    }

    bContinue_click() {
        DataStore.get().startActivityWatch(this.item.activityId, this.item.activityVariantId);
        Triggers.dispatch(TNames.JournalActivityChanged);
    }

    private dtTo(srcData: JournalActivity|PlanItem): Date | null {
        if (this.type === 'journal') {
            return (srcData.useTimeWatcher && srcData.value)
                ? DateTime.add(srcData.dtStart, srcData.value)
                : null;
        } else {
            if (!srcData.useTimeWatcher) return null;
            return DateTime.add(this.day, srcData.repeating.secondsAM);
        }
    }

    private dtFrom(srcData: JournalActivity|PlanItem) {
        return this.type === 'journal'
            ? srcData.dtStart
            : DateTime.add(this.day, srcData.repeating.secondsAM + srcData.value)
    }
}